import { useCallback, useEffect, useRef, useState } from 'react'
import { SECTION_LOCK_HEARTBEAT_MS, SECTION_LOCK_TTL_MS } from '@assessments/shared'
import { ApiError, isAssessmentSubmitted } from '../../services/apiError.js'
import { registerExitHook } from '../../services/exitHooks.js'
import {
  acquireSectionLock,
  heartbeatSectionLock,
  releaseSectionLock,
  releaseSectionLockOnClose,
  LockConflictError,
} from './lockApi.js'

// `blocked` = never held it (409 on acquire); `lost` = held it, then it expired or
// was taken over; `stale` = held it, but no beat has succeeded within the TTL, so the
// server has almost certainly freed it; `unauthenticated` = the platform session
// ended (401); `forbidden` = the session is fine but this user may no longer edit
// (403); `submitted` = the assessment was submitted, so no one may edit it again;
// `error` = any other acquire failure (network/5xx).
export type SectionLockStatus =
  | 'idle'
  | 'acquiring'
  | 'held'
  | 'blocked'
  | 'lost'
  | 'stale'
  | 'unauthenticated'
  | 'forbidden'
  | 'submitted'
  | 'error'

export interface UseSectionLockState {
  status: SectionLockStatus
  isHeld: boolean
  expiresAt: string | null
  /** Throws if the release request fails; status stays 'held' so a retry still succeeds. */
  release: () => Promise<void>
  /** Re-attempt the acquire. Succeeds only if the lock is actually free. */
  retry: () => Promise<void>
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'input'] as const

/** Statuses from which returning activity should re-attempt an acquire. */
const RECLAIMABLE: readonly SectionLockStatus[] = ['lost', 'blocked', 'stale']

/** Owns a section's edit lock lifecycle: acquire on mount, activity-gated heartbeat
 * while held, release on unmount/pagehide/app exit, re-acquire on activity after loss. */
export function useSectionLock(
  organizationSurveyId: string,
  sectionId: string,
  enabled: boolean,
  onSessionExpired?: () => void,
): UseSectionLockState {
  const [status, setStatus] = useState<SectionLockStatus>(enabled ? 'acquiring' : 'idle')
  const [expiresAt, setExpiresAt] = useState<string | null>(null)

  const statusRef = useRef<SectionLockStatus>(status)
  statusRef.current = status
  const interactedSinceBeat = useRef(false)
  const beatInFlight = useRef(false)
  const reacquireInFlight = useRef(false)
  // Wall-clock of the last acquire/heartbeat the server confirmed. Once it is older
  // than the TTL the lock is gone server-side, whatever this client last saw.
  const lastBeatOkAt = useRef<number | null>(null)
  // Held in a ref so a caller passing an inline callback doesn't re-run the effect.
  const onSessionExpiredRef = useRef(onSessionExpired)
  onSessionExpiredRef.current = onSessionExpired
  // Bumped every time the owning effect tears down (unmount, id change, or disabled).
  // An acquire() in flight when that happens must not resurrect this dead lifecycle.
  const generationRef = useRef(0)
  // Target of the most recent acquire() call; null once torn down with no follow-up.
  // Distinguishes a StrictMode double-invoke (same lock re-acquired) from a dead
  // lifecycle without racing on promise resolution order.
  const latestAcquireTargetRef = useRef<{ orgId: string; sectionId: string } | null>(null)

  const transition = useCallback((next: SectionLockStatus, expires: string | null = null) => {
    statusRef.current = next
    setStatus(next)
    setExpiresAt(expires)
  }, [])

  // Shared by the acquire and heartbeat paths so a dead session, a revoked permission,
  // and a dropped connection can never collapse into the same outcome.
  const classifyFailure = useCallback(
    (err: unknown, conflictStatus: 'blocked' | 'lost'): void => {
      if (err instanceof LockConflictError) {
        transition(conflictStatus)
        return
      }
      if (isAssessmentSubmitted(err)) {
        transition('submitted')
        return
      }
      if (err instanceof ApiError && err.status === 401) {
        transition('unauthenticated')
        onSessionExpiredRef.current?.()
        return
      }
      if (err instanceof ApiError && err.status === 403) {
        transition('forbidden')
        return
      }
      transition('error')
    },
    [transition],
  )

  const acquire = useCallback(async () => {
    const myGeneration = generationRef.current
    // Captured by closure so a stale completion targets the lock it actually acquired.
    const targetOrgId = organizationSurveyId
    const targetSectionId = sectionId
    latestAcquireTargetRef.current = { orgId: targetOrgId, sectionId: targetSectionId }
    transition('acquiring')
    try {
      const result = await acquireSectionLock(targetOrgId, targetSectionId)
      if (generationRef.current !== myGeneration) {
        const latest = latestAcquireTargetRef.current
        if (latest?.orgId === targetOrgId && latest?.sectionId === targetSectionId) {
          // A newer attempt for this same lock is in flight — let it decide the outcome.
          return
        }
        // Dead lifecycle with no follow-up attempt — release the lock nothing will read again.
        void releaseSectionLock(targetOrgId, targetSectionId).catch(() => {})
        return
      }
      lastBeatOkAt.current = Date.now()
      transition('held', result.lock.expiresAt)
    } catch (err) {
      if (generationRef.current !== myGeneration) return
      classifyFailure(err, 'blocked')
    }
  }, [organizationSurveyId, sectionId, transition, classifyFailure])

  useEffect(() => {
    if (!enabled) return

    void acquire()

    const beat = async () => {
      if (statusRef.current !== 'held' || beatInFlight.current) return
      interactedSinceBeat.current = false
      beatInFlight.current = true
      try {
        const result = await heartbeatSectionLock(organizationSurveyId, sectionId)
        lastBeatOkAt.current = Date.now()
        setExpiresAt(result.lock.expiresAt)
      } catch (err) {
        // A conflict means the lock is gone; a 401 the session; a 403 the caller's edit
        // permission. Anything else is transient — leave the status for the staleness
        // check to judge rather than stripping a working editor on one dropped packet.
        const conclusive =
          err instanceof LockConflictError ||
          isAssessmentSubmitted(err) ||
          (err instanceof ApiError && (err.status === 401 || err.status === 403))
        if (conclusive) classifyFailure(err, 'lost')
      } finally {
        beatInFlight.current = false
      }
    }

    // Stops the UI claiming a lock the server has already freed, whether the tab went
    // idle or the beats have simply been failing.
    const expireIfStale = () => {
      if (statusRef.current !== 'held') return false
      const last = lastBeatOkAt.current
      if (last === null || Date.now() - last < SECTION_LOCK_TTL_MS) return false
      transition('stale')
      return true
    }

    const interval = window.setInterval(() => {
      if (expireIfStale()) return
      // Activity-gated: no interaction since the last tick ⇒ no beat — the lock is
      // left to expire server-side exactly like an abandoned session's.
      if (statusRef.current !== 'held' || !interactedSinceBeat.current) return
      void beat()
    }, SECTION_LOCK_HEARTBEAT_MS)

    const onActivity = () => {
      interactedSinceBeat.current = true
      if (expireIfStale()) return
      // Returning activity attempts one re-acquire; it succeeds only if the lock is
      // free — the returning editor has no priority over a new holder.
      if (RECLAIMABLE.includes(statusRef.current) && !reacquireInFlight.current) {
        reacquireInFlight.current = true
        void acquire().finally(() => {
          reacquireInFlight.current = false
        })
      }
    }
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      onActivity()
      // Returning to the tab is the moment a holder most needs the truth, so beat
      // unconditionally rather than waiting up to a full interval for the next tick.
      if (statusRef.current === 'held') void beat()
    }
    const onPageHide = () => {
      // A bfcache-eligible pagehide doesn't destroy this JS context — a later
      // pageshow restore must not resume believing it still holds a lock this
      // already gave up.
      if (statusRef.current === 'held') {
        releaseSectionLockOnClose(organizationSurveyId, sectionId)
        transition('idle')
      }
    }
    const releaseIfHeld = () => {
      if (statusRef.current !== 'held') return
      return releaseSectionLock(organizationSurveyId, sectionId).catch(() => {})
    }

    for (const evt of ACTIVITY_EVENTS) window.addEventListener(evt, onActivity)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    const unregisterExitHook = registerExitHook(releaseIfHeld)

    return () => {
      generationRef.current += 1
      // Cleared so a stale acquire can tell teardown-with-no-follow-up from an
      // immediate remount, which re-sets this synchronously in acquire().
      latestAcquireTargetRef.current = null
      window.clearInterval(interval)
      for (const evt of ACTIVITY_EVENTS) window.removeEventListener(evt, onActivity)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      unregisterExitHook()
      void releaseIfHeld()
    }
  }, [organizationSurveyId, sectionId, enabled, acquire, transition, classifyFailure])

  const release = useCallback(async () => {
    if (statusRef.current !== 'held') return
    await releaseSectionLock(organizationSurveyId, sectionId)
    transition('idle')
  }, [organizationSurveyId, sectionId, transition])

  const retry = useCallback(async () => {
    if (statusRef.current === 'held') return
    await acquire()
  }, [acquire])

  return { status, isHeld: status === 'held', expiresAt, release, retry }
}
